import axios from 'axios';
import { DomainScanResult, ScanResponse, PaginatedResults, FilterOptions, ScanStatistics } from '@/types';
import type { AdvancedFilterOptions, BrandRiskAnalysis } from '../../../shared/types';
import { supabase } from '@/lib/supabase';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If VITE_API_URL is explicitly set, use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // In production (Vercel), use the current origin for API calls
  if (import.meta.env.PROD) {
    return window.location.origin;
  }

  // In development, use relative path so Vite proxy handles routing (enables ngrok / external access)
  return '';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for long-running scans
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Attach Supabase auth token to every request when a session exists
api.interceptors.request.use(
  async (config) => {
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'Server error';
      const err = new Error(message) as Error & { code?: string; status?: number };
      err.status = status;
      if (status === 429) {
        err.code = 'RATE_LIMIT';
      }
      throw err;
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error - please check your connection');
    } else {
      // Something else happened
      throw new Error(error.message || 'Unknown error occurred');
    }
  }
);

export const scanApi = {
  /**
   * Scan a single domain. Pass force=true to bypass the server-side TTL cache.
   */
  async scanSingle(domain: string, force = false): Promise<DomainScanResult> {
    const response = await api.post('/api/scan/single', { domain, force });
    return response.data;
  },

  /**
   * Scan multiple domains
   */
  async scanBulk(domains: string[]): Promise<ScanResponse> {
    const response = await api.post('/api/scan/bulk', { domains });
    return response.data;
  },

  /**
   * Scan variants of a domain across TLDs
   */
  async scanVariants(domain: string, options?: { tlds?: string[]; concurrency?: number }): Promise<ScanResponse> {
    const payload: any = { domain };
    if (options?.tlds) payload.tlds = options.tlds;
    if (typeof options?.concurrency === 'number') payload.concurrency = options.concurrency;
    const response = await api.post('/api/scan/variants', payload);
    return response.data;
  },

  /**
   * Scan variants with streaming results
   */
  async scanVariantsStream(
    domain: string,
    options?: { tlds?: string[]; concurrency?: number },
    onProgress?: (progress: { total: number; processed: number; successCount: number; errorCount: number }) => void,
    onResults?: (results: DomainScanResult[]) => void,
    onComplete?: (summary: { totalProcessed: number; successCount: number; errorCount: number }) => void,
    onError?: (error: string) => void
  ): Promise<void> {
    const payload: any = { domain, stream: true };
    if (options?.tlds) payload.tlds = options.tlds;
    if (typeof options?.concurrency === 'number') payload.concurrency = options.concurrency;

    try {
      const authHeaders: Record<string, string> = {};
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          authHeaders['Authorization'] = `Bearer ${session.access_token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/scan/variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
        body: JSON.stringify(payload),
        mode: 'cors',
      });

      if (!response.ok) {
        if (response.status === 429) {
          const body = await response.json().catch(() => ({}));
          const msg = body?.error || 'Daily scan limit reached';
          const err = new Error(msg) as Error & { code?: string };
          err.code = body?.code || 'SCAN_LIMIT';
          throw err;
        }
        if (response.status === 0 || response.status === 404) {
          throw new Error('CORS error: Unable to connect to the API. Please check if the server is running and CORS is configured properly.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log(">>>",data)
              switch (data.type) {
                case 'progress':
                  onProgress?.(data);
                  break;
                case 'results':
                  onResults?.(data.results);
                  onProgress?.(data.progress);
                  break;
                case 'complete':
                  onComplete?.(data);
                  break;
                case 'error':
                  onError?.(data.error || data.message || 'Unknown error');
                  break;
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.warn('Streaming failed, falling back to non-streaming API:', error);

      // Fallback to non-streaming API
      try {
        const fallbackResponse = await scanApi.scanVariants(domain, options);
        if (fallbackResponse.results) {
          onResults?.(fallbackResponse.results);
          onComplete?.({
            totalProcessed: fallbackResponse.totalProcessed,
            successCount: fallbackResponse.successCount,
            errorCount: fallbackResponse.errorCount
          });
        }
      } catch (fallbackError) {
        onError?.(error instanceof Error ? error.message : 'Streaming failed');
      }
    }
  },

  /**
   * Get scan results with filtering and pagination
   */
  async getResults(filters: FilterOptions | AdvancedFilterOptions = {}): Promise<PaginatedResults<DomainScanResult>> {
    const params = new URLSearchParams();

    // Check if we have advanced filters (including pricing filters)
    const hasAdvancedFilters = 'regex' in filters || 'booleanQuery' in filters || 'tlds' in filters ||
                              'lengthMin' in filters || 'lengthMax' in filters || 'registrar' in filters ||
                              'dateFrom' in filters || 'dateTo' in filters || 'valuationMin' in filters ||
                              'valuationMax' in filters || 'priceMin' in filters || 'priceMax' in filters ||
                              'excludeUnavailablePricing' in filters || 'premiumOnly' in filters ||
                              'purchasableOnly' in filters || 'sortBy' in filters || 'sortOrder' in filters;

    // Basic filters
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    // Advanced filters
    if ('regex' in filters && filters.regex) params.append('regex', filters.regex);
    if ('booleanQuery' in filters && filters.booleanQuery) params.append('booleanQuery', filters.booleanQuery);
    if ('tlds' in filters && filters.tlds && filters.tlds.length > 0) {
      params.append('tlds', filters.tlds.join(','));
    }
    if ('lengthMin' in filters && filters.lengthMin !== undefined) params.append('lengthMin', filters.lengthMin.toString());
    if ('lengthMax' in filters && filters.lengthMax !== undefined) params.append('lengthMax', filters.lengthMax.toString());
    if ('registrar' in filters && filters.registrar) params.append('registrar', filters.registrar);
    if ('dateFrom' in filters && filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if ('dateTo' in filters && filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
    if ('valuationMin' in filters && filters.valuationMin !== undefined) params.append('valuationMin', filters.valuationMin.toString());
    if ('valuationMax' in filters && filters.valuationMax !== undefined) params.append('valuationMax', filters.valuationMax.toString());

    // Pricing filters
    if ('priceMin' in filters && filters.priceMin !== undefined) params.append('priceMin', filters.priceMin.toString());
    if ('priceMax' in filters && filters.priceMax !== undefined) params.append('priceMax', filters.priceMax.toString());
    if ('excludeUnavailablePricing' in filters && filters.excludeUnavailablePricing) params.append('excludeUnavailablePricing', 'true');
    if ('premiumOnly' in filters && filters.premiumOnly) params.append('premiumOnly', 'true');
    if ('purchasableOnly' in filters && filters.purchasableOnly) params.append('purchasableOnly', 'true');

    if ('sortBy' in filters && filters.sortBy) params.append('sortBy', filters.sortBy);
    if ('sortOrder' in filters && filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    // Use advanced endpoint if we have advanced filters, otherwise use basic endpoint
    const endpoint = hasAdvancedFilters ? '/api/results/advanced' : '/api/results';
    const response = await api.get(`${endpoint}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get a specific scan result by ID
   */
  async getResultById(id: string): Promise<DomainScanResult> {
    const response = await api.get(`/api/results/${id}`);
    return response.data;
  },

  /**
   * Delete a scan result by ID
   */
  async deleteResult(id: string): Promise<void> {
    await api.delete(`/api/results/${id}`);
  },

  /**
   * Get scan statistics
   */
  async getStatistics(): Promise<ScanStatistics> {
    const response = await api.get('/api/results/statistics');
    return response.data;
  },

  /**
   * Get popular keywords
   */
  async getPopularKeywords(limit: number = 10, days: number = 30): Promise<Array<{
    keyword: string;
    searchCount: number;
    searchedAt: string;
  }>> {
    const response = await api.get('/api/keywords/popular', {
      params: { limit, days }
    });
    return response.data.keywords || [];
  },

  /**
   * Get expiring domains from CSV
   */
  async getExpiringDomains(): Promise<Array<{
    domain_name: string;
    created_date: string;
    expiring_date: string;
    price: number;
    tld: string;
    traffic_data: string;
  }>> {
    const response = await api.get('/api/expiring-domains');
    return response.data || [];
  },

  /**
   * Get scoring weights and configuration
   */
  async getScoringWeights(): Promise<any> {
    const response = await api.get('/api/results/scoring-weights');
    return response.data;
  },

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string; uptime: number }> {
    const response = await api.get('/health');
    return response.data;
  },

  async getRadarRegistrars(): Promise<{
    registrars: Array<{
      registrar: string;
      label: string;
      status: 'operational' | 'degraded' | 'unknown';
      lastRecordAt: string | null;
      recordsLast24h: number;
      tracked: boolean;
      hourlyActivity: number[];
    }>;
    cachedAt: string;
  }> {
    const response = await api.get('/api/radar/registrars');
    return response.data;
  },

  async getRadarServices(): Promise<{
    services: Array<{
      id: string;
      name: string;
      category: 'dns' | 'ssl' | 'hosting' | 'cdn' | 'email' | 'cloud';
      status: 'operational' | 'degraded' | 'outage' | 'unknown';
      description: string;
      statusPageUrl: string;
      lastCheckedAt: string;
    }>;
    polledAt: string;
  }> {
    const response = await api.get('/api/radar/services');
    return response.data;
  },

  async getRadarNews(limit = 30): Promise<{
    news: Array<{
      id: string;
      url: string;
      title: string;
      summary: string;
      source: string;
      publishedAt: string;
      savedAt: string;
    }>;
    cachedAt: string;
  }> {
    const response = await api.get('/api/radar/news', { params: { limit } });
    return response.data;
  },

  /**
   * Analyze brand risk for a domain with streaming progress updates
   */
  async analyzeBrandRisk(
    domain: string,
    onProgress?: (step: string, progress: number) => void,
    onComplete?: (analysis: BrandRiskAnalysis) => void,
    onError?: (error: string) => void
  ): Promise<BrandRiskAnalysis> {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('Starting brand risk analysis for:', domain);
        console.log('API Base URL:', API_BASE_URL);
        console.log('Full URL:', `${API_BASE_URL}/api/scan/brand-risk/analyze`);

        const response = await fetch(`${API_BASE_URL}/api/scan/brand-risk/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({ domain }),
          mode: 'cors',
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Brand risk API error:', response.status, errorText);
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  console.log('Brand risk SSE data:', data);

                  switch (data.type) {
                    case 'connected':
                      console.log('Connected to brand risk analysis stream');
                      break;
                    case 'progress':
                      onProgress?.(data.step, data.progress);
                      break;
                    case 'complete':
                      onComplete?.(data.analysis);
                      resolve(data.analysis);
                      return;
                    case 'error':
                      const errorMsg = data.error || data.message || 'Brand risk analysis failed';
                      onError?.(errorMsg);
                      reject(new Error(errorMsg));
                      return;
                    case 'end':
                      console.log('Brand risk analysis stream ended');
                      return;
                  }
                } catch (parseError) {
                  console.warn('Failed to parse brand risk SSE data:', parseError);
                }
              }
            }
          }
        };

        processStream().catch(error => {
          console.error('Brand risk analysis stream error:', error);
          onError?.(error.message);
          reject(error);
        });

      } catch (error) {
        console.error('Brand risk analysis error:', error);
        const errorMsg = error instanceof Error ? error.message : 'Brand risk analysis failed';
        onError?.(errorMsg);
        reject(new Error(errorMsg));
      }
    });
  },

  async createCheckoutSession(billing: 'monthly' | 'yearly'): Promise<{ url: string }> {
    const response = await api.post('/api/payments/checkout', { billing });
    return response.data;
  },

  async getPortalUrl(): Promise<{ url: string }> {
    const response = await api.get('/api/payments/portal');
    return response.data;
  },
};

export default api;
